import React, { Component, Suspense, lazy, createContext } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Snowplow } from 'components'
import { ThemeContext } from '@cred/react-theme'
import * as helpersActions from 'utils/redux/helpers/actions'
import * as SCREEN_TYPES from 'utils/redux/helpers/screen-types'
import { extractHostname } from 'utils/urls'
import { v4 as uuid } from 'uuid'
import styles from './App.module.scss'

const StudentLoanRefiApp = lazy(() => import('./student-loan-refi/App'))
const StudentLoanApp = lazy(() => import('./student-loan/App'))
const StudentLoanCombinedApp = lazy(() => import('./student-loan-combined/App'))
const PersonalApp = lazy(() => import('./personal/App'))
const CreditCardsApp = lazy(() => import('./credit-card/App'))
const MortgageRefiApp = lazy(() => import('./mortgage-refi/App'))
const MortgageCombinedApp = lazy(() => import('./mortgage-combined/App'))
const MortgageApp = lazy(() => import('./mortgage/App'))
const MortgageRateTableApp = lazy(() => import('./mortgage/rate-table/App'))

const WidgetContext = createContext(null)

class App extends Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    helpers: PropTypes.object.isRequired,
    clientLibrary: PropTypes.object.isRequired,
    clientInfoService: PropTypes.object.isRequired,
    query: PropTypes.object,
    params: PropTypes.object,
  }

  static defaultProps = {
    query: {},
    params: {},
  }

  constructor(props) {
    super(props)

    this.state = {
      // Options provided by partner.
      // These fallback values we need for:
      // - test widget in QA environment when host page is missing;
      // - local development.
      // Once widget is embedded into partner site these options will be replaced
      // by partners specific.
      options: {
        ui: {
          theme: props.query.theme,
        },
        product: {
          marketplace: props.params.marketplace,
          type: props.params.widgetType,
          variation: props.query.variation,
        },
      },

      // Global Snowplow context provided by Credible SDK.
      parentPage: {
        partnerReferrer: document.referrer,
        partnerDomain: extractHostname(document.referrer),
        partnerURL: document.referrer,
      },
      credclid: props.query.credclid || uuid(),
      renderId: uuid(),
      source: props.query.source,
    }
  }

  async componentDidMount() {
    const { clientLibrary } = this.props
    // Analytics initialization
    Snowplow.initialize()

    // Device type checking
    this.onWindowResize()
    window.addEventListener('resize', this.onWindowResize)

    await this.setOptionsSuppliedByPartner()

    await this.setSnowplowContext()

    this.scrollToTop()

    clientLibrary.sendMessage('onInit')
  }

  componentWillUnmount() {
    const { clientLibrary } = this.props

    window.removeEventListener('resize', this.onWindowResize)
    clientLibrary.sendMessage('onDestroy')
  }

  async setOptionsSuppliedByPartner() {
    const { clientInfoService } = this.props
    const options = await clientInfoService.optionsDeferred

    this.setState({
      options,
    })
  }

  async setSnowplowContext() {
    const { clientInfoService } = this.props
    const parentPage = await clientInfoService.snowplowContextDeferred

    this.setState({
      parentPage,
    })
  }

  scrollToTop() {
    const { clientLibrary } = this.props
    // Request client library perform scroll
    clientLibrary.sendMessage('scrollRequest', 'top')
  }


  onWindowResize = () => {
    const {
        actions: {
          helpers: {
            changeScreenResolutionToDesktop,
            changeScreenResolutionToTablet,
            changeScreenResolutionToMobile,
          },
        },
        helpers: { screenType },
      } = this.props,
      { innerWidth } = window,
      { MOBILE, TABLET, DESKTOP } = SCREEN_TYPES
    let newScreenType

    if (innerWidth < 768) {
      newScreenType = MOBILE
    } else if (innerWidth >= 768 && innerWidth < 1024) {
      newScreenType = TABLET
    } else if (innerWidth >= 1024) {
      newScreenType = DESKTOP
    }

    if (newScreenType !== screenType) {
      switch (newScreenType) {
        case DESKTOP:
          changeScreenResolutionToDesktop()
          break
        case TABLET:
          changeScreenResolutionToTablet()
          break
        case MOBILE:
          changeScreenResolutionToMobile()
          break
        default:
          break
      }
    }
  }

  render() {
    const { options, parentPage, credclid, renderId, source } = this.state,
      theme = options.ui.theme === 'fox' ? 'fox1' : options.ui.theme

    return (
      <div className={styles.container}>
        <WidgetContext.Provider value={{ options, parentPage, credclid, renderId, source }}>
          <ThemeContext.Provider value={theme}>
            <Suspense fallback={null}>
              <Switch>
                <Route
                  path="/student-loan-refi/:widgetType"
                  exact
                  render={({ match }) => (
                    <StudentLoanRefiApp widgetType={match.params.widgetType} />
                  )}
                />
                <Route
                  path="/student-loan-combined/:widgetType"
                  exact
                  render={({ match }) => (
                    <StudentLoanCombinedApp widgetType={match.params.widgetType} />
                  )}
                />
                <Route
                  path="/student-loan/:widgetType"
                  exact
                  render={({ match }) => (
                    <StudentLoanApp widgetType={match.params.widgetType} />
                  )}
                />
                <Route
                  path="/credit-card/:widgetType"
                  exact
                  render={({ match }) => (
                    <CreditCardsApp widgetType={match.params.widgetType} />
                  )}
                />
                <Route
                  path="/personal-loan/:widgetType"
                  exact
                  render={({ match }) => (
                    <PersonalApp widgetType={match.params.widgetType} />
                  )}
                />
                <Route
                  path="/mortgage-refi/:widgetType"
                  exact
                  render={({ match }) => (
                    <MortgageRefiApp widgetType={match.params.widgetType} />
                  )}
                />
                <Route
                  path="/mortgage-combined/rate-table"
                  exact
                  render={({ match }) => (
                    <MortgageRateTableApp widgetType={match.params.widgetType} />
                  )}
                />
                <Route
                  path="/mortgage-combined/:widgetType"
                  exact
                  render={({ match }) => (
                    <MortgageCombinedApp widgetType={match.params.widgetType} />
                  )}
                />
                <Route
                  path="/mortgage/:widgetType"
                  exact
                  render={({ match }) => (
                    <MortgageApp widgetType={match.params.widgetType} />
                  )}
                />
                <Redirect to="/" />
              </Switch>
            </Suspense>
          </ThemeContext.Provider>
        </WidgetContext.Provider>
      </div>
    )
  }
}

const mapStateToProps = ({ helpers }) => ({
  helpers,
})

const mapDispatchToProps = (dispatch) => ({
  actions: {
    helpers: bindActionCreators(helpersActions, dispatch),
  },
})

export { App, WidgetContext }

export default connect(mapStateToProps, mapDispatchToProps)(App)
